import type Highcharts from 'highcharts';

import type { BlockThemeConfig, DChartInterface } from 'webapp-types';
import { formatNumber } from 'webapp/utils/number';

import type VisualizationProgress from 'webapp/models-custom/visualization/progress';
import ProgressOrGaugeBaseRenderer from 'webapp/d-chart/renderer/progress-or-gauge-base';

const GAP = 8;
const SCROLLBAR_WIDTH = 7; // must match @scrollbarWidth in functions.less

export default class ProgressRenderer extends ProgressOrGaugeBaseRenderer {
	override tooltipArrowPosition = 'left'; // bcos it opens to the right

	chartStart = 0;
	chartEnd = 100;

	override get visualization(): VisualizationProgress {
		return super.visualization;
	}

	get series() {
		return this.visualization.series;
	}

	private get barHeight(): number {
		const m = this.dChart.sizeModifier;
		if (this.series.length <= 1) {
			return m * 2;
		}
		const availableHeight = this.dChart.maxHeight;
		const heightPerMetric = availableHeight / this.series.length;
		return Math.max(4, Math.min(heightPerMetric * 0.25, m * 2));
	}

	private get labelOffset(): number {
		return this.barHeight / 2 + 4;
	}

	private get margin(): number {
		return Math.round(this.dChart.sizeModifier * 1.2 * 0.5) + 4;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;
		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				type: 'bar',
				events: {
					render: function (this: Highcharts.Chart) {
						that.updateDynamicElementsOnRender(this);
					},
				},
			},
			yAxis: [
				{
					labels: {
						autoRotation: false as unknown as number[], // false: no auto rotation; Highcharts' types only allow number[]
					},
					min: this.chartStart,
					max: this.chartEnd,
					tickAmount: 3,
				},
				{
					labels: {
						autoRotation: false as unknown as number[], // false: no auto rotation; Highcharts' types only allow number[]
					},
					linkedTo: 0,
					opposite: true,
					tickPositions: [this.chartStart, this.chartEnd], // showing tick on start and end - to proper position labels (name, period)
				},
			],
			series: [
				{
					animation: {
						duration: this.dChart.isAnimated ? 1000 : 0,
					},
					grouping: false,
					zIndex: 2,
				},
				{
					animation: false, // prevent animating second series (progress bar), animate only first series (progress indicator) - animation defined renderer
					zIndex: 1,
				},
			],
			plotOptions: {
				series: {
					events: {
						click: function (
							this: Highcharts.Series,
							event: Highcharts.SeriesClickEventObject,
						) {
							that.onPointClick(
								(event.point as DChartInterface.ChartHoverPoint).dbxPoint,
								event,
								event.srcElement as SVGElement,
							);
						},
					},
				},
			},
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const series = this.series;

		if (!series.length) {
			return {
				xAxis: { categories: [] },
				yAxis: [{ visible: false }, { visible: false }],
				series: [{ data: [] }, { data: [] }],
			};
		}

		const that = this;
		const categories: string[] = [];
		const progressData: DChartInterface.ChartDataPoint[] = [];
		const backgroundData: Highcharts.PointOptionsObject[] = [];

		series.forEach((serie, idx) => {
			const { primaryPoint, minPoint, maxPoint } = this.getPointsForSeries(serie);
			const periodEndTS = serie.periodEndTS;

			if (!primaryPoint) {
				return;
			}

			categories.push(serie.title || `Metric ${idx + 1}`);

			const percent = this.getValueProgressForSeries(serie);
			const shownValue = Math.max(0, Math.min(percent, 100));
			const isInverted = this.visualization.isInverted;
			const goalStatus = maxPoint?.goalData?.goalStatus ?? null;
			const stepId = this.getGradientStepIdForPercent(percent, isInverted, goalStatus);

			progressData.push({
				x: idx,
				y: shownValue,
				dbxPoint: primaryPoint,
				className: `step${this.isTrafficLightsEnabled ? '-traffic-light' : ''}-${stepId}${primaryPoint.onClick ? ' point-clickable' : ''}`,
				dataLabels: {
					enabled: true,
					useHTML: true,
					align: 'left',
					verticalAlign: 'middle',
					y: 0,
					crop: false,
					overflow: 'allow',
					formatter: function () {
						const title = that.sanitize(serie.title || '');
						const valueStr = primaryPoint.valueStr;
						const { max } = that.getMinMaxValues(minPoint!, maxPoint!);

						const maxValueStr = max.valueStr || '';
						// position labels relative to bar center
						const plotWidth = that.dChart.hChart?.plotWidth || that.dChart.maxWidth;
						const rightInset = SCROLLBAR_WIDTH + 4;
						const leftOffset = -(shownValue / that.chartEnd) * plotWidth;

						let percentBubble = '';
						const formattedPercent = primaryPoint.comparePoints?.find(
							(point) => point.isGoal,
						)?.changeStr;

						if (formattedPercent) {
							percentBubble = `<div class="progress-goal"><div class="highcharts-dbx-percent-bubble">${formattedPercent}</div><span> of your goal</span></div>`;
						} else if (percent) {
							percentBubble = `<div class="progress-goal"><div class="highcharts-dbx-percent-bubble">${formatNumber(percent, '#.[##]')}%</div><span> of your goal</span></div>`;
						}

						const maxDisplay =
							maxPoint && !maxPoint.loading && maxValueStr
								? `<span class="progress-max">${maxValueStr}</span>`
								: typeof max.value === 'number'
									? `<span class="progress-max">${formatNumber(max.value, primaryPoint.format, { scale: primaryPoint.scale })}</span>`
									: '';

						const hideTimeLeft = that.visualization.blockSetup.block.hideTimeLeft;
						const timeLeft = hideTimeLeft ? '' : that.formatTimeLeft(periodEndTS);
						const timeLeftHtml = timeLeft
							? `<span class="progress-time-left">${timeLeft}</span>`
							: '';

						return (
							`<div class="progress-label">` +
							`<div class="progress-top" style="bottom:${that.labelOffset}px;left:${leftOffset}px;width:${plotWidth - rightInset}px">` +
							`<span class="progress-title">${title}</span>` +
							`${timeLeftHtml}` +
							`</div>` +
							`<div class="progress-below" style="top:${that.labelOffset}px;left:${leftOffset}px;width:${plotWidth - rightInset}px">` +
							`<span class="progress-value">${valueStr}</span>` +
							`${percentBubble}` +
							`${maxDisplay}` +
							`</div>` +
							`</div>`
						);
					},
				},
			});

			backgroundData.push({
				x: idx,
				y: this.chartEnd,
			});
		});

		return {
			xAxis: {
				categories,
				visible: false,
			},
			yAxis: [
				{
					visible: false,
				},
				{
					visible: false,
				},
			],
			series: [
				{
					className: '', // reset single-metric class
					data: progressData,
				},
				{
					data: backgroundData,
				},
			],
		};
	}

	override get sizeOptions(): Highcharts.Options {
		const pointWidth = this.barHeight;
		const margin = this.margin;

		let options = super.sizeOptions;

		options = this.mergeOptions(options, {
			chart: {
				marginTop: margin,
				marginBottom: margin,
			},
			series: [
				{
					pointWidth,
					borderRadius: {
						radius: pointWidth / 2,
						where: 'all',
					},
				},
				{
					pointWidth,
					borderRadius: {
						radius: pointWidth / 2,
						where: 'all',
					},
				},
			],
		});

		return options;
	}

	get gradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData,
			themeId = themeData.themeId,
			colors = themeData.colors,
			steps =
				(themeData.visualizationConfig.steps as
					BlockThemeConfig.GradientStep[] | undefined) || [],
			baseColorId = themeData.visualizationConfig.base || 0,
			trafficLightsColors = themeData.trafficLightsColors,
			trafficLightsSteps = themeData.visualizationConfig.trafficLightsSteps || [];

		const gradientDefs: DChartInterface.Def[] = [
			{
				tagName: 'linearGradient',
				id: `background-progress-bar-${themeId}-${baseColorId}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						'stop-color': colors[baseColorId],
						'stop-opacity': 0.1,
					},
				],
			},
		];

		steps.forEach((step, index: number) => {
			const stepId = index + 1;

			gradientDefs.push({
				tagName: 'linearGradient',
				id: `gradient-progress-bar-step-${stepId}-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: step.range[0],
						'stop-color': colors[step.gradient[0]],
					},
					{
						tagName: 'stop',
						offset: step.range[1],
						'stop-color': colors[step.gradient[1]],
					},
				],
			});
		});

		trafficLightsSteps.forEach((step, index: number) => {
			const stepId = index + 1;

			gradientDefs.push({
				tagName: 'linearGradient',
				id: `gradient-progress-bar-step-traffic-light-${stepId}-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 0,
				y2: 1,
				children: [
					{
						tagName: 'stop',
						offset: 0,
						'stop-color': trafficLightsColors[step.gradient[0]],
					},
					{
						tagName: 'stop',
						offset: 1,
						'stop-color': trafficLightsColors[step.gradient[1]],
					},
				],
			});
		});

		return gradientDefs;
	}

	override updateData(): void {
		const options = this.mergeOptions(this.dataOptions, this.sizeOptions);
		this.dChart.updateHChart(options);
	}

	// ------- dynamic height adjustment --------

	private _isAdjustingHeight = false;

	private updateDynamicElementsOnRender(hChart: Highcharts.Chart): void {
		if (this._isAdjustingHeight) return;

		const node = this.dChart.node;
		const seriesCount = this.series.length;
		if (!seriesCount) return;

		// measure actual label heights from rendered data labels
		const labels = node.querySelectorAll<HTMLElement>('.progress-label');
		if (!labels.length) return;

		let maxTopHeight = 0;
		let maxBelowHeight = 0;

		labels.forEach((label) => {
			const topEl = label.querySelector('.progress-top');
			const belowEl = label.querySelector('.progress-below');
			if (topEl) {
				const h = topEl.getBoundingClientRect().height;
				if (h > maxTopHeight) maxTopHeight = h;
			}
			if (belowEl) {
				const h = belowEl.getBoundingClientRect().height;
				if (h > maxBelowHeight) maxBelowHeight = h;
			}
		});

		if (!maxTopHeight && !maxBelowHeight) return;

		const labelOffset = this.labelOffset;
		const gap = GAP;
		const margin = this.margin;
		const edgeMargins = margin * 2;

		const metricHeight = Math.max(
			2 * labelOffset + maxTopHeight + maxBelowHeight + gap,
			2 * (labelOffset + maxBelowHeight),
		);

		// scroll container (.block) padding extends the visible area
		const blockNode = node.closest('.block');
		const scrollPaddingBottom = blockNode
			? parseFloat(window.getComputedStyle(blockNode).paddingBottom) || 0
			: 0;

		// space from bar center to bottom of value labels
		const bottomExtent = labelOffset + maxBelowHeight;
		const visibleArea = this.dChart.maxHeight + scrollPaddingBottom;
		const usableSpace = visibleArea - margin - bottomExtent;

		// how many complete metrics fit without clipping
		const visibleMetricCount = Math.min(
			seriesCount,
			Math.max(1, Math.floor(usableSpace / metricHeight)),
		);

		let chartHeight: number;

		if (seriesCount > visibleMetricCount) {
			// expand chart height so all metrics are reachable via scroll
			const adjustedMetricHeight = Math.max(
				metricHeight,
				usableSpace / (visibleMetricCount - 0.5),
			);
			chartHeight = Math.round(seriesCount * adjustedMetricHeight + edgeMargins);
		} else {
			chartHeight = this.dChart.maxHeight;
		}

		const willScroll = chartHeight > this.dChart.maxHeight;
		let chartWidth: number;

		if (willScroll) {
			node.style.height = `${chartHeight}px`;
			chartWidth = node.clientWidth;
		} else {
			chartWidth = this.dChart.maxWidth;
		}

		const needsHeightUpdate = Math.abs(hChart.chartHeight - chartHeight) > 2;
		const needsWidthUpdate = Math.abs(hChart.chartWidth - chartWidth) > 2;

		if (needsHeightUpdate || needsWidthUpdate) {
			this._isAdjustingHeight = true;
			try {
				hChart.setSize(chartWidth, chartHeight);
			} finally {
				this._isAdjustingHeight = false;
			}
		}

		// always sync container/node CSS heights
		// hChart.setSize may not update the container CSS when called from DChart.setSize
		// leaving it stuck at a stale value.
		const resolvedHeight = `${hChart.chartHeight}px`;
		if (hChart.container.style.height !== resolvedHeight) {
			hChart.container.style.height = resolvedHeight;
		}

		if (node.style.height !== resolvedHeight) {
			node.style.height = resolvedHeight;
		}
	}
}
